import { create } from 'zustand';
import { 
  ReferralLink, 
  ReferralTracking, 
  CommissionPayment, 
  InfluencerDashboardData,
  InfluencerAnalytics,
  GenerateReferralLinkRequest,
  InfluencerPerformanceResponse,
  AdminInfluencerAnalyticsResponse
} from '../types/influencer';
import { influencerService } from '../services/influencerService';

interface InfluencerState {
  // Personal Data
  referralLink: ReferralLink | null;
  dashboardData: InfluencerDashboardData | null;
  earnings: {
    total: number;
    pending: number;
    thisMonth: number;
  };
  performance: {
    clicks: number;
    signups: number;
    conversions: number;
    conversionRate: number;
  };
  tier: {
    current: number;
    progress: number;
    nextTierRequirement: number;
  };
  paymentHistory: CommissionPayment[];
  
  // Admin Data
  allInfluencers: any[];
  topPerformers: any[];
  analytics: InfluencerAnalytics | null;
  
  // Email Marketing Data
  emailContacts: any[];
  emailCampaigns: any[];
  contactListResponse: any | null;
  selectedContacts: string[];
  
  // Loading States
  loading: {
    dashboard: boolean;
    referralLink: boolean;
    payments: boolean;
    analytics: boolean;
    emailContacts: boolean;
    emailCampaigns: boolean;
    uploadingContacts: boolean;
    sendingCampaign: boolean;
  };
  
  // Actions
  generateReferralLink: (customCode?: string) => Promise<void>;
  fetchDashboardData: () => Promise<void>;
  fetchPaymentHistory: () => Promise<void>;
  requestPayout: (amount: number) => Promise<void>;
  
  // Admin Actions
  fetchInfluencerAnalytics: () => Promise<void>;
  promoteUserToInfluencer: (userId: string) => Promise<void>;
  processPayouts: () => Promise<void>;
  
  // Email Marketing Actions
  uploadContacts: (contacts: Array<{ email: string; firstName?: string; lastName?: string; company?: string }>) => Promise<void>;
  getEmailContacts: (page?: number, limit?: number) => Promise<void>;
  createEmailCampaign: (campaignData: { name: string; subject: string; content: string; scheduledAt?: string }) => Promise<void>;
  sendEmailCampaign: (campaignId: string, contactIds: string[]) => Promise<void>;
  selectContacts: (contactIds: string[]) => void;
  clearSelectedContacts: () => void;
  
  // Utility Actions
  setLoading: (key: keyof InfluencerState['loading'], value: boolean) => void;
  resetState: () => void;
}

const initialState = {
  referralLink: null,
  dashboardData: null,
  earnings: { total: 0, pending: 0, thisMonth: 0 },
  performance: { clicks: 0, signups: 0, conversions: 0, conversionRate: 0 },
  tier: { current: 1, progress: 0, nextTierRequirement: 10 },
  paymentHistory: [],
  allInfluencers: [],
  topPerformers: [],
  analytics: null,
  emailContacts: [],
  emailCampaigns: [],
  contactListResponse: null,
  selectedContacts: [],
  loading: {
    dashboard: false,
    referralLink: false,
    payments: false,
    analytics: false,
    emailContacts: false,
    emailCampaigns: false,
    uploadingContacts: false,
    sendingCampaign: false,
  },
};

export const useInfluencerStore = create<InfluencerState>((set, get) => ({
  ...initialState,
  
  // Personal Actions
  generateReferralLink: async (customCode?: string) => {
    try {
      set({ loading: { ...get().loading, referralLink: true } });
      
      const request: GenerateReferralLinkRequest = { customCode };
      const response = await influencerService.generateReferralLink(request);
      
      if (response.success) {
        set({ referralLink: response.data });
      } else {
        throw new Error(response.message || 'Failed to generate referral link');
      }
    } catch (error: any) {
      console.error('Error generating referral link:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, referralLink: false } });
    }
  },
  
  fetchDashboardData: async () => {
    try {
      set({ loading: { ...get().loading, dashboard: true } });
      
      const response = await influencerService.getDashboardData();
      
      if (response.success) {
        const { data } = response;
        set({
          dashboardData: data,
          referralLink: data.referralLink,
          earnings: {
            total: data.personalStats.totalEarnings,
            pending: data.personalStats.pendingEarnings,
            thisMonth: data.personalStats.totalEarnings, // TODO: Calculate this month specifically
          },
          performance: {
            clicks: data.personalStats.totalClicks,
            signups: data.personalStats.totalSignups,
            conversions: data.personalStats.totalConversions,
            conversionRate: data.personalStats.conversionRate,
          },
          tier: {
            current: data.tier.current,
            progress: data.tier.progress,
            nextTierRequirement: data.tier.nextTierRequirement,
          },
          paymentHistory: data.paymentHistory,
        });
      } else {
        throw new Error(response.message || 'Failed to fetch dashboard data');
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, dashboard: false } });
    }
  },
  
  fetchPaymentHistory: async () => {
    try {
      set({ loading: { ...get().loading, payments: true } });
      
      const response = await influencerService.getPaymentHistory();
      
      if (response.success) {
        set({ paymentHistory: response.data });
      } else {
        throw new Error(response.message || 'Failed to fetch payment history');
      }
    } catch (error: any) {
      console.error('Error fetching payment history:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, payments: false } });
    }
  },
  
  requestPayout: async (amount: number) => {
    try {
      const response = await influencerService.requestPayout(amount);
      
      if (response.success) {
        // Refresh dashboard data to update pending earnings
        await get().fetchDashboardData();
      } else {
        throw new Error(response.message || 'Failed to request payout');
      }
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      throw error;
    }
  },
  
  // Admin Actions
  fetchInfluencerAnalytics: async () => {
    try {
      set({ loading: { ...get().loading, analytics: true } });
      
      const response = await influencerService.getInfluencerAnalytics();
      
      if (response.success && response.data) {
        set({
          analytics: response.data,
          topPerformers: response.data.topPerformers,
        });
      } else {
        throw new Error(response.message || 'Failed to fetch influencer analytics');
      }
    } catch (error: any) {
      console.error('Error fetching influencer analytics:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, analytics: false } });
    }
  },
  
  promoteUserToInfluencer: async (userId: string) => {
    try {
      const response = await influencerService.promoteUserToInfluencer(userId);
      
      if (response.success) {
        // Refresh analytics to show updated data
        await get().fetchInfluencerAnalytics();
      } else {
        throw new Error(response.message || 'Failed to promote user to influencer');
      }
    } catch (error: any) {
      console.error('Error promoting user to influencer:', error);
      throw error;
    }
  },
  
  processPayouts: async () => {
    try {
      const response = await influencerService.processPayouts();
      
      if (response.success) {
        // Refresh analytics to show updated data
        await get().fetchInfluencerAnalytics();
      } else {
        throw new Error(response.message || 'Failed to process payouts');
      }
    } catch (error: any) {
      console.error('Error processing payouts:', error);
      throw error;
    }
  },
  
  // Email Marketing Actions
  uploadContacts: async (contacts: Array<{ email: string; firstName?: string; lastName?: string; company?: string }>) => {
    try {
      set({ loading: { ...get().loading, uploadingContacts: true } });
      
      const response = await influencerService.uploadContacts(contacts);
      
      if (response.success) {
        // Refresh contact list
        await get().getEmailContacts();
      } else {
        throw new Error(response.message || 'Failed to upload contacts');
      }
    } catch (error: any) {
      console.error('Error uploading contacts:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, uploadingContacts: false } });
    }
  },

  getEmailContacts: async (page: number = 1, limit: number = 50) => {
    try {
      set({ loading: { ...get().loading, emailContacts: true } });
      
      const response = await influencerService.getEmailContacts(page, limit);
      
      set({ 
        emailContacts: response.contacts,
        contactListResponse: response
      });
    } catch (error: any) {
      console.error('Error getting email contacts:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, emailContacts: false } });
    }
  },

  createEmailCampaign: async (campaignData: { name: string; subject: string; content: string; scheduledAt?: string }) => {
    try {
      const campaign = await influencerService.createEmailCampaign(campaignData);
      set({ emailCampaigns: [...get().emailCampaigns, campaign] });
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  sendEmailCampaign: async (campaignId: string, contactIds: string[]) => {
    try {
      set({ loading: { ...get().loading, sendingCampaign: true } });
      
      const response = await influencerService.sendEmailCampaign(campaignId, contactIds);
      
      if (response.success) {
        // Refresh contact list to update statuses
        await get().getEmailContacts();
      } else {
        throw new Error(response.message || 'Failed to send campaign');
      }
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      throw error;
    } finally {
      set({ loading: { ...get().loading, sendingCampaign: false } });
    }
  },

  selectContacts: (contactIds: string[]) => {
    set({ selectedContacts: contactIds });
  },

  clearSelectedContacts: () => {
    set({ selectedContacts: [] });
  },
  
  // Utility Actions
  setLoading: (key: keyof InfluencerState['loading'], value: boolean) => {
    set({ loading: { ...get().loading, [key]: value } });
  },
  
  resetState: () => {
    set(initialState);
  },
})); 